import Alpine from "alpinejs"
import { load, store } from "../localStorage"
import validate from "../validations"
import pipeHeader from "./pipeHeader"
import pipeFooter from "../../../../../lib/common/js/components/pipeFooter"
import optionsForm from "./optionsForm"
import heightForm from "./heightForm"
import weightForm from "./weightForm"
import targetWeightForm from "./targetWeightForm"
import importantEventDate from "./importantEventDate"
import timer from "./timer"
import checkout from "./checkout"
import primer from "./primer"
import goalPace from "./goalPace"
import fasterProgress from "./fasterProgress"
import walking from "./walking"
import email from "./email"
import customPlan from "./customPlan"
import results from "./results"
import circularLoading from "./circularLoading"
import glassesOfWater from "./glassesOfWater"
import sliderInput from "./sliderInput"
import cookieConsent from "../../../../../lib/common/js/components/cookieConsent"
import basicThankYouView from "./basicThankYouView"
import checkoutPlanSelection from "./checkoutPlanSelection"
import paymentFailed from "./paymentFailed"
import acSingleOfferPaymodes from "./acSingleOfferPaymodes"
import basicThankYouViewAc from "./basicThankYouViewAc"
import sendOff from "./sendOff"
import { TrackingManager } from "../../../../../lib/common/js/services/tracking/trackingManager"
import { convertArabicDigits } from "../../../../../lib/common/js/helpers"

// Upsell imports

import premiumCheckout from "./premiumCheckout"
import premiumOffer from "./premiumOffer"
import shoppingListOffer from "./shoppingListOffer"
import shoppingListCheckout from "./shoppingListCheckout"
import chatCheckout from "./chatCheckout"
import chatOffer from "./chatOffer"
import bundleCheckout from "./bundleCheckout"
import bundleOffer from "./bundleOffer"
import upsellWorkouts from "./upsellWorkouts"
import upsellsThankYou from "./upsellsThankYou"

// Upsell for the new AC flow
import premiumOfferAc from "./premiumOfferAc"
import shoppingListOfferAc from "./shoppingListOfferAc"
import chatOfferAc from "./chatOfferAc"
import chatOfferLastChanceAc from "./chatOfferLastChanceAc"
import bundleOfferAc from "./bundleOfferAc"
import upsellsThankYouAc from "./upsellsThankYouAc"

const VIEW_STORAGE_KEY = "view"
const GLOBAL_STORAGE_KEY = "data"
const INVALID_DOMAIN_CODE = "invalid_domain"
const EMAIL_VALIDATION_CODE = "email_verification_failed"

const basicView = ({ nextStepPath, formFields, redirectIfWeightGainUrl, isReducerNeeded }) => ({
  isSubmitAllowed: false,
  showSpinner: false,
  formValues: {},
  errors: {},
  showFetchError: false,
  statusToTrack: null,
  isStoreDataChangedNeeded: false,
  store: Alpine.store("data"),
  loading: false,

  // Start: U-series code
  commonInit() {
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        this.store.reload()
        this.loading = false
        this.showSpinner = false
        this.initFormValues(formFields) // Adjust the form values after the page reload

        setTimeout(() => {
          this.initActions() // WARNING: initActions() called here!
        }, 1)
      }
    })

    setTimeout(() => {
      this.initActions()
    }, 1)

    this.initFormValues(formFields)

    if (redirectIfWeightGainUrl) {
      this.redirectIfWeightGain(redirectIfWeightGainUrl)
    }
  },

  init() {
    this.commonInit()
  },

  initActions() {
    if (isReducerNeeded) {
      this.formValues = Object.keys(this.formValues).reduce(
        (formValues, key) => ({
          ...formValues,
          [key]: this.store.values[key],
        }),
        {},
      )
    }

    if (this.onInit) this.onInit()

    this.store.trackViewAndMaybeStatus(this.statusToTrack, this.isStoreDataChangedNeeded)

    requestAnimationFrame(this.focusField)
  },

  focusField() {
    const field = document.querySelector("[autofocus]")

    if (field) field.focus()
  },

  getNextStepPath(withToken = false, nextPath) {
    if (!withToken || !this.store.values.token) return nextPath

    return `${nextStepPath}#stored-token=${this.store.values.token}`
  },

  scrollToFirstError() {
    document
      .querySelector("[data-error]")
      .scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })
  },

  handleApiError(e) {
    if (e.error === "email: Invalid email") {
      this.errors = { email: EMAIL_VALIDATION_CODE }
    } else {
      this.errors = { [e.path]: e.code }
    }
  },

  isEmailValidationError() {
    const errors = this.errors
    if (errors) {
      if (errors.email === INVALID_DOMAIN_CODE || errors.email === EMAIL_VALIDATION_CODE) {
        return true
      }
    }

    return false
  },

  // End: U-series code

  initFormValues(formFields) {
    if (!formFields) return

    const viewData = load(VIEW_STORAGE_KEY)
    const globalData = load(GLOBAL_STORAGE_KEY)

    const isActiveErrors = viewData?.errors && viewData.path === window.location.pathname

    if (isActiveErrors) {
      this.formValues = viewData.formValues
      this.errors = viewData.errors
    } else {
      formFields.map((field) => {
        const value = globalData.values[field]

        this.formValues[field] = value
      })
    }

    this.setSubmitAllowed(this.formValues, formFields)
  },

  isSelected(name, value) {
    return Boolean(this.formValues[name]?.includes(value))
  },

  isMetric() {
    return this.store.values.measurementSystem === "metric"
  },

  isImperial() {
    return this.store.values.measurementSystem === "imperial"
  },

  setMeasurementSystem(value) {
    if (!value) return

    this.store.values.measurementSystem = value
  },

  update(field, value) {
    this.formValues[field] = value
    this.submit()
  },

  updateAndRedirect(field, value, redirectPath) {
    this.formValues[field] = value
    this.submit({ redirectPath })
  },

  handleInput($event, field) {
    this.formValues[field] = $event.target.value && $event.target.value.trim()

    this.errors[field] = null

    this.setSubmitAllowed(this.formValues, formFields)
  },

  handleNumericInput($event, field) {
    $event.target.value = $event.target.value.replace(/[^\d\u0660-\u0669]/g, "")

    $event.target.value = convertArabicDigits($event.target.value)

    this.formValues[field] = $event.target.value && Number($event.target.value)

    this.errors[field] = null

    this.setSubmitAllowed(this.formValues, formFields)
  },

  getRequiredFields(formFields) {
    // Treat all formFields as required by default
    if (Array.isArray(formFields)) {
      return formFields
    }

    // Provide the ability to pass the extended config for the formFields
    return Object.keys(formFields).map((key) => {
      if (formFields[key].required) {
        return key
      }
    })
  },

  setSubmitAllowed(formValues, formFields) {
    this.validateRequired(formValues, formFields)
  },

  validateRequired(formValues, formFields) {
    const requiredFields = this.getRequiredFields(formFields)
    const allRequiredPresent = requiredFields.every((value) => {
      const formValue = formValues[value]

      if (Array.isArray(formValue)) return !!formValue.length

      return formValue && formValue !== 0
    })

    this.isSubmitAllowed = allRequiredPresent
  },

  resetErrors() {
    this.errors = {}
  },

  processFormValues() {
    return this.formValues
  },

  storeViewData(data) {
    store(VIEW_STORAGE_KEY, {
      path: window.location.pathname,
      ...data,
    })
  },

  resetTrackerUserData() {
    const internalTracker = new TrackingManager(
      window.trackingConfig.enabledTrackingServices,
      pipelineConfig,
    )

    return new Promise((resolve) => {
      internalTracker.resetUserData("klaviyo")
      setTimeout(resolve, 0) // Resolve after the synchronous operation completes to imitate async behavior
    })
  },

  async submit({ push, redirectPath, withToken, isEmailStep } = {}) {
    if (this.store.preloadPromise) {
      this.loading = true
      await this.store.preloadPromise
    }

    const nextPath = redirectPath || nextStepPath
    const formValues = this.processFormValues()

    const { hasErrors, errors } = validate(formValues, this.store)

    if (hasErrors) {
      this.storeViewData({ errors, formValues: this.formValues })

      this.errors = { ...this.errors, ...errors }
    } else {
      this.showSpinner = true
      this.isSubmitAllowed = false
      this.loading = true
      this.errors = {}
      const prevEmail = this.store.values.email
      this.store.values = { ...this.store.values, ...formValues }

      this.storeViewData(null)

      if (!push || (await this.store.push(false, null, this.handleApiError.bind(this)))) {
        if (isEmailStep && this.store.values.email !== prevEmail) {
          await this.resetTrackerUserData()
        }

        window.location = this.getNextStepPath(withToken, nextPath)
      } else {
        const isEmailError = this.isEmailValidationError()
        if (this.store.emailValidationError || isEmailError) {
          this.store.values.email = ""
          this.errors.email = true
        }
        this.showFetchError = true
        this.showSpinner = false
        this.loading = false
      }
    }
  },

  // The edge case handling, when user's target weight is bigger than the current weight
  redirectIfWeightGain(url) {
    const isMetric = this.isMetric()

    if (isMetric) {
      if (this.store.values.targetWeightKg >= this.store.values.weightKg) {
        window.location = url
        return
      }
    } else {
      if (this.store.values.targetWeightLbs >= this.store.values.weightLbs) {
        window.location = url
        return
      }
    }

    // The component's content may be hidden before init for the better UI
    this.initContentHidden = false
  },
})

export default [
  { name: "view", data: basicView },
  { name: "cookieConsent", data: cookieConsent },
  { name: "pipeHeader", data: pipeHeader },
  { name: "pipeFooter", data: pipeFooter },
  { name: "optionsForm", data: optionsForm(basicView) },
  { name: "heightForm", data: heightForm(basicView) },
  { name: "weightForm", data: weightForm(basicView) },
  { name: "targetWeightForm", data: targetWeightForm(basicView) },
  { name: "goalPace", data: goalPace(basicView) },
  { name: "fasterProgress", data: fasterProgress(basicView) },
  { name: "walking", data: walking(basicView) },
  { name: "glassesOfWater", data: glassesOfWater(basicView) },
  { name: "importantEventDate", data: importantEventDate(basicView) },
  { name: "email", data: email(basicView) },
  { name: "customPlan", data: customPlan(basicView) },
  { name: "sliderInput", data: sliderInput },
  { name: "circularLoading", data: circularLoading },
  { name: "results", data: results },
  { name: "timer", data: timer },
  { name: "checkout", data: checkout(basicView) },
  { name: "primer", data: primer },
  { name: "basicThankYouView", data: basicThankYouView(basicView) },
  { name: "premiumOffer", data: premiumOffer(basicView) },
  { name: "premiumCheckout", data: premiumCheckout },
  { name: "shoppingListOffer", data: shoppingListOffer(basicView) },
  { name: "upsellWorkouts", data: upsellWorkouts(basicView) },
  { name: "shoppingListCheckout", data: shoppingListCheckout },
  { name: "chatOffer", data: chatOffer(basicView) },
  { name: "chatCheckout", data: chatCheckout },
  { name: "bundleOffer", data: bundleOffer(basicView) },
  { name: "bundleCheckout", data: bundleCheckout },
  { name: "upsellsThankYou", data: upsellsThankYou(basicView) },
  { name: "upsellsThankYouAc", data: upsellsThankYouAc(basicView) },
  { name: "checkoutPlanSelection", data: checkoutPlanSelection(basicView) },
  { name: "paymentFailed", data: paymentFailed(basicView) },
  { name: "acSingleOfferPaymodes", data: acSingleOfferPaymodes(basicView) },
  { name: "premiumOfferAc", data: premiumOfferAc(basicView) },
  { name: "shoppingListOfferAc", data: shoppingListOfferAc(basicView) },
  { name: "chatOfferAc", data: chatOfferAc(basicView) },
  { name: "bundleOfferAc", data: bundleOfferAc(basicView) },
  { name: "chatOfferLastChanceAc", data: chatOfferLastChanceAc(basicView) },
  { name: "basicThankYouViewAc", data: basicThankYouViewAc(basicView) },
  { name: "sendOff", data: sendOff },
]
